import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import cls from 'classnames'

import { ThemeContext } from '@cred/react-theme'

import Tooltip from 'components/tooltip'
import WidgetContext from 'components/widget-context'
import { CtaButton } from 'components/cta-button'
import { PSL } from 'common/const/marketplace'

import { getInfoRows, getRedirectHrefWithParams } from './utils'
import LenderCardTop from './LenderCardTop'
import { ReactComponent as BtnIcon } from './assets/btn-icon.svg'
import styles from './LenderCard.module.scss'
import BestBaner from './BestBaner'
import { RATING_LINK } from './LenderCardTop/config'

const LenderCard = ({ lenderName, lenderSlug, lender, best = false, position, rating }) => {
  const {
    options: { uniqueId },
    parentPage: { partnerURL },
    vertical,
  } = useContext(WidgetContext)

  const theme = useContext(ThemeContext)

  const info = getInfoRows(lender, { hideMinCreditScore: theme === 'wsj' })

  const getRedirectUrl = getRedirectHrefWithParams({
    lenderSlug,
    position,
    pageUrl: partnerURL,
    uniqueId,
    vertical,
    theme,
  })
  const checkRatesUrl = getRedirectUrl('credible-marketplace')
  const redirectUrl = getRedirectUrl('redirect')
  const relAttr = theme === 'wsj' && vertical === PSL ? { rel: 'sponsored' } : {}

  const emitClickEvent = (name) => {
    const eventFullName = `lender_banner_cta_click:${name}`

    window.requestAnimationFrame(() => {
      window.parent.postMessage(eventFullName, '*')
      console.info(`[Info] - Dispatch ${eventFullName} postMessage`)
    })
  }

  return (
    <div className={styles.lenderCardWrap}>
      <BestBaner shouldShow={best} />
      <div className={styles.lenderCard}>
        <LenderCardTop
          lenderSlug={lenderSlug}
          lenderName={lenderName}
          vertical={vertical}
          rating={rating}
        />
        <div className={styles.infoWrap}>
          {info.map(({ title, value, tooltipContent }) => (
            <div key={title}>
              <p className="neutral-light disclaimer">{title}</p>
              <p className="rate-info-value">
                <span>{value} </span>
                {tooltipContent && (
                  <Tooltip
                    triggerContent={<i className="cred-icon icon-help-outline neutral400" />}
                    triggerClassName="fs-13 ml-4"
                    contentClassName={styles.tooltip}
                    analyticsOptions={{ contentSubject: RATING_LINK[theme] }}
                  >
                    {tooltipContent}
                  </Tooltip>
                )}
              </p>
            </div>
          ))}
        </div>
        <div>
          <CtaButton
            size="small"
            target="_blank"
            {...relAttr}
            className={cls(styles.directLenderCta, styles[theme])}
            href={redirectUrl}
            analytics={{
              data: { text: 'GO TO LENDER SITE', activated: true },
            }}
            onClick={() => emitClickEvent('lender')}
          >
            GO TO LENDER SITE
            <BtnIcon />
          </CtaButton>
          <p className="neutral-light align-center mt-4 disclaimer">on {lenderName}’s website</p>
          {vertical === PSL ? null : (
            <>
              <CtaButton
                size="small"
                variant="outlined"
                target="_blank"
                className={cls(styles.checkRatesCta, styles[theme])}
                href={checkRatesUrl}
                analytics={{
                  data: { text: 'COMPARE RATES', activated: true },
                }}
                onClick={() => emitClickEvent('compare_rates')}
              >
                COMPARE RATES <BtnIcon />
              </CtaButton>
              <p className="neutral-light align-center mt-4 disclaimer">
                on Credible’s website{' '}
                <Tooltip
                  triggerContent={<i className="cred-icon icon-help-outline" />}
                  triggerClassName="fs-13"
                  contentClassName="fs-13 align-left neutral500"
                  analyticsOptions={{ contentSubject: 'on Credible’s website' }}
                >
                  Credible lets you check with multiple student loan lenders to get rates with no
                  impact to your credit score. Visit their website to take the next steps.
                </Tooltip>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

LenderCard.propTypes = {
  lenderName: PropTypes.string.isRequired,
  lenderSlug: PropTypes.string.isRequired,
  position: PropTypes.number.isRequired,
  lender: PropTypes.object,
  best: PropTypes.bool,
  rating: PropTypes.number,
}

export default LenderCard
